import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Col, Input, message, Modal, Row, Select } from 'antd';
import { Field, Form, Formik } from 'formik';
import * as yup from 'yup';

import DateTimePicker from 'components/DateTimePicker';
import ModalFormFooter from 'components/Form/ModalFormFooter';
import {
  FakeUploadButton,
  FormItem,
  InputContentHolder,
  InputLimitWidth
} from 'components/Layout/Form';
import Label from 'components/Text/Label';
import TimezonePicker from 'components/TimezonePicker';
import { makeSelectDefaultCurrency, makeSelectTenantTimezone } from 'containers/App/selectors';

import { convertUrlToFile, formItemLayout } from '../utils';
import { createBDOSetting, getBDOSetting, updateBDOSetting } from './action';
import { creditCardsGet } from './creditCard/actions';
import {
  CreateBDOSettingFormValues,
  CreateBDOSettingPayload,
  IBDOSetting,
  IBDOSettingsState,
  ICreditCard,
  UpdateBDOSettingPayload
} from './types';

interface FormValues extends CreateBDOSettingFormValues {}

const loyaltyItemTypes = ['airmile', 'hotel_loyalty'];

const validationSchema = yup.object().shape({
  name: yup.string().trim().required('Name is required'),
  conversion_rate: yup
    .number()
    .typeError('Conversion Rate must be a number')
    .positive('Conversion Rate must be greater than zero')
    .required('Conversion Rate is required'),
  min_redeem: yup
    .number()
    .typeError('Minimum Redeem Amount must be a number')
    .integer('Minimum Redeem Amount must be an integer')
    .positive('Minimum Redeem Amount must be greater than zero')
    .required('Minimum Redeem Amount is required'),
  increment_amount: yup
    .number()
    .typeError('Points Redeem Increment must be a number')
    .integer('Points Redeem Increment must be an integer')
    .positive('Points Redeem Increment must be greater than zero')
    .required('Points Redeem Increment is required'),
  lrs_reward_code: yup
    .string()
    .matches(/^[a-zA-Z0-9]+$/, 'LRS Reward Code must contain only letters and numbers')
    .required('LRS Reward Code is required'),
  loyalty_acct_placeholder: yup
    .string()
    .trim()
    .max(75, 'Loyalty Acct No Placeholder must be at most 75 characters')
    .when('item_type', {
      is: value => loyaltyItemTypes.includes(value),
      then: schema => schema.required('Loyalty Acct No Placeholder is required')
    }),
  loyalty_points_name: yup
    .string()
    .trim()
    .max(50, 'Loyalty Acct Currency must be at most 50 characters')
    .when('item_type', {
      is: value => loyaltyItemTypes.includes(value),
      then: schema => schema.required('Loyalty Acct Currency is required')
    }),
  card_ids: yup
    .array()
    .min(1, 'Applicable Credit Cards are required')
    .required('Applicable Credit Cards are required'),
  start_date: yup
    .date()
    .nullable()
    .required('Start Date is required')
    .typeError('Invalid Start Date'),
  end_date: yup
    .date()
    .nullable()
    .required('End Date is required')
    .typeError('Invalid End Date')
    .min(yup.ref('start_date'), 'End Date must be after Start Date')
});

const BDOSettingAddEditModal = ({
  title,
  selectedBDOSetting = null,
  BDOSettingType,
  cardItemType,
  modalOpen,
  isEdit = false,
  closeModal
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const fetchedBDOSettingData: IBDOSetting | null = useSelector(
    (state: { bdoSettings: IBDOSettingsState }) => state.bdoSettings.bdoSetting
  );
  const { creditCards }: { creditCards } = useSelector(
    (state: { creditCards: { creditCards: ICreditCard[] } }) => state.creditCards
  );
  const defaultCurrency: string = useSelector(makeSelectDefaultCurrency());
  const tenantTimezone: string = useSelector(makeSelectTenantTimezone()) || 'UTC';

  const defaultCurrencyLC = (defaultCurrency ?? 'USD').toLowerCase();
  const currencyValues = ['USD', 'PHP'];
  const showLoyaltyFields = loyaltyItemTypes.includes(cardItemType);

  const cardOptions =
    creditCards?.data?.map(card => ({
      label: card.product_code,
      value: card.id
    })) || [];

  useEffect(() => {
    if (modalOpen) {
      dispatch(creditCardsGet({ size: 200 }));
    }
  }, [modalOpen, dispatch]);

  useEffect(() => {
    if (modalOpen && selectedBDOSetting) {
      dispatch(getBDOSetting(selectedBDOSetting));
    }
  }, [modalOpen, selectedBDOSetting, dispatch]);

  const initialValues: FormValues = {
    name:
      isEdit && fetchedBDOSettingData
        ? fetchedBDOSettingData?.name
        : !isEdit && selectedBDOSetting
        ? `Copy of ${fetchedBDOSettingData?.name}`
        : '',
    description: fetchedBDOSettingData?.description || '',
    image: fetchedBDOSettingData?.image
      ? typeof fetchedBDOSettingData?.image === 'string'
        ? { url: fetchedBDOSettingData?.image }
        : fetchedBDOSettingData?.image
      : null,
    conversion_rate: fetchedBDOSettingData?.conversion_rate || null,
    min_redeem: fetchedBDOSettingData?.min_redeem || null,
    increment_amount: fetchedBDOSettingData?.increment_amount || null,
    lrs_reward_code: fetchedBDOSettingData?.lrs_reward_code || '',
    loyalty_acct_placeholder: fetchedBDOSettingData?.loyalty_acct_placeholder || '',
    loyalty_points_name: fetchedBDOSettingData?.loyalty_points_name || '',
    card_ids: fetchedBDOSettingData?.cards?.map(card => card?.id) || [],
    currency: fetchedBDOSettingData?.currency || defaultCurrencyLC,
    start_date: fetchedBDOSettingData?.start_date
      ? new Date(fetchedBDOSettingData?.start_date)
      : null,
    end_date: fetchedBDOSettingData?.end_date ? new Date(fetchedBDOSettingData?.end_date) : null,
    item_type: cardItemType
  };

  const getCardItemTypeTag = type => {
    switch (type) {
      case 'airmile':
        return 'airmiles';
      case 'cash_credit':
        return 'cash-credit';
      case 'hotel_loyalty':
        return 'hotels';
      default:
        return 'unknown';
    }
  };

  const cardItemTypeTag = getCardItemTypeTag(cardItemType);

  const onSubmit = async (
    values: FormValues,
    {
      setSubmitting,
      resetForm
    }: { setSubmitting: (isSubmitting: boolean) => void; resetForm: () => void }
  ) => {
    setSubmitting(true);
    const apiValues: CreateBDOSettingPayload | UpdateBDOSettingPayload = {
      ...values,
      name: values.name?.trim(),
      description: values.description?.trim(),
      loyalty_points_name: values.loyalty_points_name?.trim(),
      loyalty_acct_placeholder: values.loyalty_acct_placeholder?.trim(),
      conversion_rate: values.conversion_rate as number,
      min_redeem: values.min_redeem as number,
      increment_amount: values.increment_amount as number,
      start_date: values.start_date
        ? typeof values.start_date === 'string'
          ? values.start_date
          : values.start_date.toISOString()
        : '',
      end_date: values.end_date
        ? typeof values.end_date === 'string'
          ? values.end_date
          : values.end_date.toISOString()
        : '',
      item_type: cardItemType as any
    };

    const onSuccess = () => {
      setSubmitting(false);
      resetForm();
      closeModal();
      if (!isEdit) {
        navigate(`/p/credit_card_redemption_settings/${cardItemTypeTag}/list`);
      }
    };

    const onError = () => {
      setSubmitting(false);
    };

    if (isEdit && selectedBDOSetting) {
      dispatch(
        updateBDOSetting(
          { ...(apiValues as UpdateBDOSettingPayload), id: selectedBDOSetting },
          onSuccess,
          onError
        )
      );
    } else {
      if (cardItemType !== 'cash_credit') {
        if (!values.image?.file) {
          if (values.image?.url) {
            try {
              const fileFromUrl = await convertUrlToFile(values.image.url);
              apiValues.images = [{ file: fileFromUrl, filename: values.name }];
            } catch (error) {
              message.error('The image from the provided link could not be processed.');
              setSubmitting(false);
              return;
            }
          } else {
            message.error('An image file is required.');
            setSubmitting(false);
            return;
          }
        }
      }
      dispatch(createBDOSetting(apiValues as CreateBDOSettingPayload, onSuccess, onError));
      navigate(`/p/credit_card_redemption_settings/${cardItemTypeTag}/list`);
    }
  };

  return (
    <Modal
      maskClosable={false}
      destroyOnClose
      open={modalOpen}
      title={isEdit ? `Update ${title}` : `Add ${title}`}
      footer={null}
      onCancel={closeModal}
      width={1000}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({ isSubmitting, resetForm, setFieldValue }) => {
          return (
            <Form>
              <Field name={'name'}>
                {({ field, meta }) => {
                  const hasError = meta.error && meta.touched;
                  return (
                    <FormItem
                      {...formItemLayout}
                      label={'Name'}
                      validateStatus={hasError ? 'error' : undefined}
                      hasFeedback={!hasError && meta.touched}
                      help={hasError && <>{meta.error}</>}
                      required
                    >
                      <Input allowClear placeholder={`${BDOSettingType} Program Name`} {...field} />
                    </FormItem>
                  );
                }}
              </Field>
              <Field name={'description'}>
                {({ field, meta }) => {
                  const hasError = meta.error && meta.touched;
                  return (
                    <FormItem
                      {...formItemLayout}
                      label={'Description'}
                      validateStatus={hasError ? 'error' : undefined}
                      hasFeedback={!hasError && meta.touched}
                      help={hasError && <>{meta.error}</>}
                      required={false}
                    >
                      <Input
                        allowClear
                        placeholder={'Enter a description (not visible to customers)'}
                        {...field}
                      />
                    </FormItem>
                  );
                }}
              </Field>
              {showLoyaltyFields && (
                <>
                  <Field name={'loyalty_points_name'}>
                    {({ field, meta }) => {
                      const hasError = meta.error && meta.touched;
                      return (
                        <FormItem
                          {...formItemLayout}
                          label={'Loyalty Acct Currency'}
                          validateStatus={hasError ? 'error' : undefined}
                          hasFeedback={!hasError && meta.touched}
                          help={hasError && <>{meta.error}</>}
                          required
                        >
                          <Input
                            allowClear
                            maxLength={50}
                            placeholder={
                              cardItemType === 'airmile'
                                ? 'Name of the Airmile Points'
                                : 'Name of the Hotel Loyalty Points'
                            }
                            {...field}
                          />
                        </FormItem>
                      );
                    }}
                  </Field>
                  <Field name={'loyalty_acct_placeholder'}>
                    {({ field, meta }) => {
                      const hasError = meta.error && meta.touched;
                      return (
                        <FormItem
                          {...formItemLayout}
                          label={'Loyalty Acct No Placeholder'}
                          validateStatus={hasError ? 'error' : undefined}
                          hasFeedback={!hasError && meta.touched}
                          help={hasError && <>{meta.error}</>}
                          required
                        >
                          <Input
                            allowClear
                            maxLength={75}
                            placeholder={
                              cardItemType === 'airmile'
                                ? 'Placeholder Text for the Airmile Program Number in the Input Form'
                                : 'Placeholder Text for the Hotel Loyalty Program Number in the Input Form'
                            }
                            {...field}
                          />
                        </FormItem>
                      );
                    }}
                  </Field>
                </>
              )}
              {BDOSettingType !== 'CashCredit' && (
                <Field name="image">
                  {({ field, meta }) => {
                    return (
                      <>
                        <FormItem label="Logo" required {...formItemLayout}>
                          <div style={{ display: 'block', textAlign: 'left' }}>
                            <label htmlFor="image_uploads">
                              <FakeUploadButton>Upload Image</FakeUploadButton>
                            </label>
                          </div>
                          <input
                            id="image_uploads"
                            style={{
                              opacity: 0,
                              width: 0,
                              height: 0,
                              position: 'absolute',
                              transform: 'translateX(-100vw)'
                            }}
                            type="file"
                            accept="image/png"
                            onChange={e => {
                              const file = e.target.files[0];
                              if (file) {
                                if (file.type !== 'image/png') {
                                  message.error('Only PNG files are allowed.');
                                  return;
                                }
                                const objectUrl = URL.createObjectURL(file);
                                setFieldValue(field.name, { file, url: objectUrl });
                              }
                            }}
                          />
                          {field.value && field.value.url && (
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                marginTop: '10px'
                              }}
                            >
                              <img
                                src={field.value.url}
                                style={{
                                  maxWidth: 'min(120px, 15vw)',
                                  maxHeight: 'min(80px, 10vh)',
                                  height: 'auto',
                                  objectFit: 'contain',
                                  borderRadius: '4px',
                                  padding: '5px'
                                }}
                                alt="Credit card preview"
                              />
                            </div>
                          )}
                        </FormItem>
                      </>
                    );
                  }}
                </Field>
              )}
              <Field name={'conversion_rate'}>
                {({ field, meta }) => {
                  const hasError = meta.error && meta.touched;
                  return (
                    <FormItem
                      {...formItemLayout}
                      label={'Conversion Rate'}
                      validateStatus={hasError ? 'error' : undefined}
                      hasFeedback={!hasError && meta.touched}
                      help={hasError && <>{meta.error}</>}
                      required
                    >
                      <Row align="middle" gutter={16}>
                        <Col>
                          <Input
                            type="number"
                            min={0}
                            step="any"
                            allowClear
                            placeholder={'Conversion Rate'}
                            {...field}
                          />
                        </Col>
                        {BDOSettingType === 'CashCredit' && (
                          <Col>
                            <Label> Credit Card Points = 1 Currency </Label>
                          </Col>
                        )}
                        {BDOSettingType === 'Hotel' ? (
                          <Col>
                            <Label> {BDOSettingType} Loyalty points = 1 Credit Card Point </Label>
                          </Col>
                        ) : BDOSettingType !== 'CashCredit' ? (
                          <Col>
                            <Label>Credit Card Point = 1 {BDOSettingType} point</Label>
                          </Col>
                        ) : (
                          <></>
                        )}
                      </Row>
                    </FormItem>
                  );
                }}
              </Field>
              {BDOSettingType === 'CashCredit' && (
                <Field name={'currency'}>
                  {({ field, meta }) => {
                    const hasError = meta.error && meta.touched;
                    return (
                      <FormItem
                        {...formItemLayout}
                        label={'Currency'}
                        validateStatus={hasError ? 'error' : undefined}
                        hasFeedback={!hasError && meta.touched}
                        help={hasError && <>{meta.error}</>}
                        required={false}
                      >
                        <InputContentHolder>
                          <InputLimitWidth>
                            <Field
                              name="currency"
                              component={({ field, form, ...props }) => (
                                <Select
                                  {...field}
                                  onBlur={e => e.preventDefault()}
                                  onChange={value => {
                                    setFieldValue(field.name, value);
                                  }}
                                >
                                  {currencyValues.map(item => (
                                    <Select.Option key={item} value={item.toLowerCase()}>
                                      {item}
                                    </Select.Option>
                                  ))}
                                </Select>
                              )}
                            />
                          </InputLimitWidth>
                        </InputContentHolder>
                      </FormItem>
                    );
                  }}
                </Field>
              )}
              <Field name={'min_redeem'}>
                {({ field, meta }) => {
                  const hasError = meta.error && meta.touched;
                  return (
                    <FormItem
                      {...formItemLayout}
                      label={'Min. Redeem Amt'}
                      validateStatus={hasError ? 'error' : undefined}
                      hasFeedback={!hasError && meta.touched}
                      help={hasError && <>{meta.error}</>}
                      required
                    >
                      <Row align="middle" gutter={16}>
                        <Col>
                          <Input
                            type="number"
                            min={0}
                            step="1"
                            precision={0}
                            allowClear
                            placeholder={'Min Redeem Amt'}
                            {...field}
                          />
                        </Col>
                      </Row>
                    </FormItem>
                  );
                }}
              </Field>
              <Field name={'increment_amount'}>
                {({ field, meta }) => {
                  const hasError = meta.error && meta.touched;
                  return (
                    <FormItem
                      {...formItemLayout}
                      label={'Pts Redeem Increment'}
                      validateStatus={hasError ? 'error' : undefined}
                      hasFeedback={!hasError && meta.touched}
                      help={hasError && <>{meta.error}</>}
                      required
                    >
                      <Row align="middle" gutter={16}>
                        <Col>
                          <Input
                            type="number"
                            min={0}
                            step="1"
                            precision={0}
                            allowClear
                            placeholder={'Pts Redeem Increment'}
                            {...field}
                          />
                        </Col>
                      </Row>
                    </FormItem>
                  );
                }}
              </Field>
              <Field name={'lrs_reward_code'}>
                {({ field, meta }) => {
                  const hasError = meta.error && meta.touched;
                  return (
                    <FormItem
                      {...formItemLayout}
                      label={'LRS Reward Code'}
                      validateStatus={hasError ? 'error' : undefined}
                      hasFeedback={!hasError && meta.touched}
                      help={hasError && <>{meta.error}</>}
                      required
                    >
                      <Input
                        allowClear
                        placeholder={'Enter LRS Reward Code (not visible to customers)'}
                        {...field}
                      />
                    </FormItem>
                  );
                }}
              </Field>
              <Field name={'card_ids'}>
                {({ field, meta }) => {
                  const hasError = meta.error && meta.touched;
                  return (
                    <FormItem
                      {...formItemLayout}
                      label={'Applicable Credit Cards'}
                      validateStatus={hasError ? 'error' : undefined}
                      hasFeedback={!hasError && meta.touched}
                      help={hasError && <>{meta.error}</>}
                      required
                    >
                      <Select
                        {...field}
                        allowClear
                        style={{ minWidth: 300, maxWidth: 800 }}
                        size="large"
                        mode="multiple"
                        options={cardOptions}
                        onChange={value => {
                          setFieldValue(field.name, value);
                        }}
                      />
                    </FormItem>
                  );
                }}
              </Field>
              <FormItem {...formItemLayout} label="Start date" required>
                <Row gutter={16} align="middle">
                  <Field name="start_date">
                    {({ field, form, meta }) => {
                      const hasError = meta.error && meta.touched;
                      return (
                        <FormItem
                          validateStatus={hasError ? 'error' : undefined}
                          hasFeedback={!hasError && meta.touched}
                          help={hasError && <>{meta.error}</>}
                          style={{ marginBottom: 0 }}
                        >
                          <DateTimePicker
                            value={field.value}
                            onChange={value => form.setFieldValue('start_date', value)}
                            datePickerId="start_date_date_picker"
                            timePickerId="start_date_time_picker"
                            timezone={tenantTimezone}
                            showDate
                            showTime
                          />
                        </FormItem>
                      );
                    }}
                  </Field>
                  <Col span={8}>
                    <TimezonePicker value={tenantTimezone} disabled={true} />
                  </Col>
                </Row>
              </FormItem>
              <FormItem {...formItemLayout} label="End date" required>
                <Row gutter={16} align="middle">
                  <Field name="end_date">
                    {({ field, form, meta }) => {
                      const hasError = meta.error && meta.touched;
                      return (
                        <FormItem
                          validateStatus={hasError ? 'error' : undefined}
                          hasFeedback={!hasError && meta.touched}
                          help={hasError && <>{meta.error}</>}
                          style={{ marginBottom: 0 }}
                        >
                          <DateTimePicker
                            value={field.value}
                            onChange={value => form.setFieldValue('end_date', value)}
                            datePickerId="end_date_date_picker"
                            timePickerId="end_date_time_picker"
                            timezone={tenantTimezone}
                            showDate
                            showTime
                          />
                        </FormItem>
                      );
                    }}
                  </Field>
                  <Col span={8}>
                    <TimezonePicker value={tenantTimezone} disabled={true} />
                  </Col>
                </Row>
              </FormItem>
              <ModalFormFooter>
                <Button
                  type="default"
                  onClick={() => {
                    resetForm();
                    closeModal();
                  }}
                >
                  Cancel
                </Button>
                <Button htmlType="submit" loading={isSubmitting} type="primary">
                  Save
                </Button>
              </ModalFormFooter>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default BDOSettingAddEditModal;
